// dagLayouts — the SIX graph layouts + the animated view/layout morph.
//
// Ported from the /dag_design prototype's `layoutAll()`, `deoverlap()`,
// `forceLayout()` and the `ease()`/`step()`/requestAnimationFrame tween used by
// `setMode()`/`setLayout()`. The prototype baked node `rank`/`order`/`ocount`
// into a hand-authored `flightNodes()` table; here the layouts are PURE
// functions over the *real* provenance graph — rank is derived by longest-path
// layering from the edges, and the in-rank order/fan-width is derived from the
// node ordering — so the engine reads the same at 6 nodes or 6,000.
//
// All positions are normalized to [0,1] in both axes (the explorer maps them to
// the diagram box with CSS `left%`/`top%`). The collision-relaxation and
// force-directed passes are deterministic (no Math.random), so a given graph +
// mode always yields the same positions — which is what the vitest pins.
//
// The animation helper (`createLayoutMorph` + `ease`) is what makes switching
// layout OR view "flip and animate": it tweens every node from its old position
// map to its new one over a duration, via requestAnimationFrame, so the nodes
// glide instead of jumping. Edges follow for free because the explorer recomputes
// edge transforms from the (animating) node positions each frame.

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

/** Normalized position in the unit square. */
export interface Point {
  x: number;
  y: number;
}

/** id → position map. The currency of every layout + the morph tween. */
export type Positions = Record<string, Point>;

/** The six layout modes from the prototype's `layoutAll()`. */
export type LayoutMode = "tb" | "bt" | "lr" | "rl" | "force" | "radial";

/** All six modes, in toolbar order (the explorer renders a button per entry). */
export const LAYOUT_MODES: readonly LayoutMode[] = ["tb", "bt", "lr", "rl", "force", "radial"];

/**
 * A layout-engine node. The explorer's `DagExplorerNode` and the canonical
 * `GraphNode` both satisfy this (only `id` is required). `rank`/`order`/`ocount`
 * are honored if a caller pre-computed them (e.g. a hand-authored topology);
 * otherwise they're derived from the edges.
 */
export interface LayoutNode {
  id: string;
  /** Pre-computed layer (0 = root/sink … high = source). Derived if absent. */
  rank?: number;
  /** Pre-computed in-rank slot index. Derived if absent. */
  order?: number;
  /** Pre-computed count of siblings in the same rank. Derived if absent. */
  ocount?: number;
}

/**
 * A layout-engine edge. Accepts the prototype's `[from, to]` tuple, the
 * explorer's `{from,to}` / `{source,target}` / `{parent_id,child_id}` object,
 * or the canonical `{source,target}` — whatever the caller already has.
 */
export type LayoutEdge =
  | [string, string]
  | {
      from?: string;
      to?: string;
      source?: string;
      target?: string;
      parent_id?: string;
      child_id?: string;
    };

// ─────────────────────────────────────────────────────────────────────────────
// Edge normalization + rank/order derivation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalize any accepted edge shape into a `[parent, child]` tuple, matching the
 * prototype's convention where an edge runs parent→child (e.g. `['plot','lin']`
 * means plot's parent is lin, i.e. lin is upstream of plot). Returns null for an
 * edge whose endpoints don't both resolve to known node ids.
 */
function normalizeEdge(e: LayoutEdge, known: Set<string>): [string, string] | null {
  let a: string | undefined;
  let b: string | undefined;
  if (Array.isArray(e)) {
    [a, b] = e;
  } else {
    a = e.from ?? e.source ?? e.parent_id;
    b = e.to ?? e.target ?? e.child_id;
  }
  if (a == null || b == null || a === b) return null;
  if (!known.has(a) || !known.has(b)) return null;
  return [a, b];
}

/**
 * Derive `rank` for every node by longest-path layering. The prototype's ranks
 * run 0 (the sink/plot the user reads) up to N (the leaf sources). With the
 * `parent→child` edge convention, "upstream" of a node `f` is the set of `b`
 * with an edge `f→b` (the prototype's `ancestorsOf`). So a node's rank is its
 * longest upstream distance from any sink (a node that is no edge's child, i.e.
 * never appears as the `b`/upstream endpoint). Cycles (the opt↔rans iteration
 * loop in the topo dataset) are broken by a visited-guard so the pass always
 * terminates.
 */
function deriveRanks(ids: string[], edges: [string, string][]): Map<string, number> {
  // upstream adjacency: f → [its upstream neighbours]
  const up = new Map<string, string[]>();
  const isUpstreamOfSomething = new Set<string>(); // appears as `b` (an upstream endpoint)
  for (const id of ids) up.set(id, []);
  for (const [f, b] of edges) {
    up.get(f)!.push(b);
    isUpstreamOfSomething.add(b);
  }
  // Sinks: nodes that are nobody's upstream → rank 0 roots. If the graph is a
  // pure cycle with no sink, fall back to the first id so we still terminate.
  const sinks = ids.filter((id) => !isUpstreamOfSomething.has(id));
  const seeds = sinks.length > 0 ? sinks : ids.length > 0 ? [ids[0]!] : [];

  const rank = new Map<string, number>();
  for (const id of ids) rank.set(id, 0);
  // Longest-path relaxation: rank(b) = max(rank(b), rank(f)+1) along f→b.
  // Bounded iterations (|V|) so a cyclic graph can't loop forever.
  const stack: Array<{ id: string; depth: number }> = seeds.map((id) => ({ id, depth: 0 }));
  const guard = ids.length + 1; // max sensible depth; clamps runaway cycles
  while (stack.length > 0) {
    const { id, depth } = stack.pop()!;
    if (depth > guard) continue;
    for (const nb of up.get(id) ?? []) {
      const nd = depth + 1;
      if (nd > (rank.get(nb) ?? 0)) {
        rank.set(nb, nd);
        stack.push({ id: nb, depth: nd });
      }
    }
  }
  return rank;
}

/**
 * Resolve each node's `(rank, order, ocount)`. Honors any pre-computed values on
 * the node (the prototype's hand-authored topology path); otherwise derives rank
 * from the edges and assigns a stable in-rank order by first-seen index so fans
 * spread evenly and deterministically.
 */
interface Placed {
  id: string;
  rank: number;
  order: number;
  ocount: number;
}

function placeNodes(nodes: LayoutNode[], edges: [string, string][]): Placed[] {
  const ids = nodes.map((n) => n.id);
  const derived = deriveRanks(ids, edges);
  // rank per node (honor a pre-baked rank if present)
  const rankOf = new Map<string, number>();
  for (const n of nodes) rankOf.set(n.id, n.rank ?? derived.get(n.id) ?? 0);

  // group by rank in first-seen order so `order`/`ocount` are stable
  const byRank = new Map<number, LayoutNode[]>();
  for (const n of nodes) {
    const r = rankOf.get(n.id)!;
    (byRank.get(r) ?? byRank.set(r, []).get(r)!).push(n);
  }
  const placed: Placed[] = [];
  for (const [r, group] of byRank) {
    const ocount = group.length;
    group.forEach((n, i) => {
      placed.push({
        id: n.id,
        rank: r,
        order: n.order ?? i,
        ocount: n.ocount ?? ocount,
      });
    });
  }
  // keep input node order so callers can rely on a stable iteration
  const indexOf = new Map(ids.map((id, i) => [id, i] as const));
  placed.sort((a, b) => indexOf.get(a.id)! - indexOf.get(b.id)!);
  return placed;
}

// ─────────────────────────────────────────────────────────────────────────────
// Collision relaxation (deoverlap) — ported verbatim in spirit from the
// prototype's `deoverlap()`. Pushes node boxes apart so they never overlap in
// any layout, in the normalized unit-square coordinate space.
// ─────────────────────────────────────────────────────────────────────────────

/** Reference box geometry (px) used to derive the minimum center separation. */
const DEOVERLAP = {
  S: 600, // reference graph px (the square diagram area)
  bw: 110, // node box width px
  bh: 50, // node box height px
  gap: 12, // min gap px
  iterations: 600, // enough passes for the dense force/radial clouds to settle
  min: 0.06,
  max: 0.94,
} as const;

/**
 * Push node boxes apart until no pair overlaps in the normalized unit square.
 * Runs a fixed (deterministic) number of relaxation passes and then, if any
 * overlap survives the clamp-into-bounds (which can re-introduce one when many
 * boxes pile against an edge — the failure mode that bit force/radial), runs a
 * GUARANTEED settle: it widens the working bounds just enough to fit the row/
 * column the boxes need, finishes relaxing, then rescales the whole cloud back
 * into [min,max]. The result always clears the min separation, stays in-bounds,
 * and is deterministic for a given input.
 */
function deoverlap(pos: Positions): Positions {
  const { S, bw, bh, gap, min, max } = DEOVERLAP;
  const mx = (bw + gap) / S; // min center separation in x, normalized
  const my = (bh + gap) / S; // min center separation in y, normalized
  // first-seen order so the parity tie-break (and thus the output) is stable
  const P = Object.keys(pos).map((id) => ({ id, x: pos[id]!.x, y: pos[id]!.y }));

  // Geometric futility guard. The [min,max] square only fits about
  // floor(span/mx) * floor(span/my) boxes (~32 at the reference geometry), so
  // well beyond that the clamped relax can NEVER settle — every pass finds
  // thousands of overlaps, the clamp shoves boxes straight back, and all
  // 600+400 O(n^2) passes burn for nothing (8.7e9 pair-checks at 4k nodes =
  // a 60-90 s frozen main thread, FXC-9009). The wide-box rescue is no rescue
  // at that scale either: its rescale shrinks the cloud so far the separation
  // is destroyed again. Past ~4x capacity, return the layout untouched — the
  // base layouts already spread nodes, and an overlapping-but-instant field
  // beats a frozen tab.
  const roomSpan = max - min;
  const capacity = Math.floor(roomSpan / mx) * Math.floor(roomSpan / my);
  if (P.length > capacity * 4) return pos;

  const relax = (lo: number, hi: number, iterations: number): boolean => {
    for (let it = 0; it < iterations; it += 1) {
      let moved = false;
      for (let i = 0; i < P.length; i += 1) {
        for (let j = i + 1; j < P.length; j += 1) {
          const a = P[i]!;
          const b = P[j]!;
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          if (Math.abs(dx) < mx && Math.abs(dy) < my) {
            const penX = mx - Math.abs(dx);
            const penY = my - Math.abs(dy);
            if (penX <= penY) {
              // tie-break a perfect x-overlap deterministically by parity of i
              const s = (dx < 0 ? -1 : 1) * (penX / 2) || (i % 2 ? 1 : -1) * (mx / 2);
              a.x -= s;
              b.x += s;
            } else {
              const s = (dy < 0 ? -1 : 1) * (penY / 2) || (i % 2 ? 1 : -1) * (my / 2);
              a.y -= s;
              b.y += s;
            }
            moved = true;
          }
        }
      }
      for (const p of P) {
        p.x = Math.max(lo, Math.min(hi, p.x));
        p.y = Math.max(lo, Math.min(hi, p.y));
      }
      if (!moved) return true; // fully settled
    }
    return false;
  };

  const anyOverlap = (): boolean => {
    for (let i = 0; i < P.length; i += 1) {
      for (let j = i + 1; j < P.length; j += 1) {
        const a = P[i]!;
        const b = P[j]!;
        if (Math.abs(b.x - a.x) < mx - 1e-9 && Math.abs(b.y - a.y) < my - 1e-9) return true;
      }
    }
    return false;
  };

  relax(min, max, DEOVERLAP.iterations);

  // If clamping against the [min,max] box left residual overlaps (too many boxes
  // for the available room — force/radial clusters), relax again in a roomy box
  // sized to the worst case, then rescale that cloud back into [min,max] so the
  // separation is preserved while the field still fills the panel.
  if (anyOverlap()) {
    const n = P.length || 1;
    // a box big enough to lay every node on a grid at the min separation
    const grid = Math.ceil(Math.sqrt(n));
    const wide = Math.max(max - min, grid * Math.max(mx, my) * 1.1);
    relax(min, min + wide, 400);

    // rescale the relaxed cloud back into [min,max] (uniform, keeps proportions)
    let mnx = Infinity;
    let mxx = -Infinity;
    let mny = Infinity;
    let mxy = -Infinity;
    for (const p of P) {
      mnx = Math.min(mnx, p.x);
      mxx = Math.max(mxx, p.x);
      mny = Math.min(mny, p.y);
      mxy = Math.max(mxy, p.y);
    }
    const span = max - min;
    const sx = mxx - mnx || 1;
    const sy = mxy - mny || 1;
    for (const p of P) {
      p.x = min + (span * (p.x - mnx)) / sx;
      p.y = min + (span * (p.y - mny)) / sy;
    }
  }

  const out: Positions = {};
  for (const p of P) out[p.id] = { x: p.x, y: p.y };
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Force-directed layout — ported from the prototype's `forceLayout()`. A
// deterministic spring/repulsion relaxation (fixed iteration count, fixed seed
// ring start, no randomness), normalized into [0.1, 0.9] then deoverlapped.
// ─────────────────────────────────────────────────────────────────────────────

const FORCE = {
  L: 0.24, // spring rest length
  kRep: 0.011, // repulsion constant
  kSpr: 0.11, // spring constant
  iterations: 360,
  maxStep: 0.035,
  pad: 0.1, // normalized inner padding
} as const;

function forceLayout(placed: Placed[], edges: [string, string][]): Positions {
  const n = placed.length;
  if (n === 0) return {};
  if (n === 1) return { [placed[0]!.id]: { x: 0.5, y: 0.5 } };

  const idx = new Map<string, number>();
  placed.forEach((p, i) => idx.set(p.id, i));
  // deterministic seed: an even ring (the prototype seeds the same way)
  const P = placed.map((_, i) => ({
    x: 0.5 + 0.34 * Math.cos((i / n) * 6.2832),
    y: 0.5 + 0.34 * Math.sin((i / n) * 6.2832),
  }));
  const { L, kRep, kSpr, iterations, maxStep, pad } = FORCE;
  // only edges whose endpoints are both placed
  const E = edges.filter(([u, v]) => idx.has(u) && idx.has(v));
  const clamp = (v: number) => Math.max(-maxStep, Math.min(maxStep, v));

  for (let it = 0; it < iterations; it += 1) {
    const F = P.map(() => ({ x: 0, y: 0 }));
    // pairwise repulsion
    for (let a = 0; a < n; a += 1) {
      for (let b = a + 1; b < n; b += 1) {
        let dx = P[a]!.x - P[b]!.x;
        let dy = P[a]!.y - P[b]!.y;
        const d = Math.hypot(dx, dy) || 0.001;
        const f = kRep / (d * d);
        dx /= d;
        dy /= d;
        F[a]!.x += dx * f;
        F[a]!.y += dy * f;
        F[b]!.x -= dx * f;
        F[b]!.y -= dy * f;
      }
    }
    // spring attraction along edges
    for (const [u, v] of E) {
      const a = idx.get(u)!;
      const b = idx.get(v)!;
      let dx = P[b]!.x - P[a]!.x;
      let dy = P[b]!.y - P[a]!.y;
      const d = Math.hypot(dx, dy) || 0.001;
      const f = kSpr * (d - L);
      dx /= d;
      dy /= d;
      F[a]!.x += dx * f;
      F[a]!.y += dy * f;
      F[b]!.x -= dx * f;
      F[b]!.y -= dy * f;
    }
    for (let i = 0; i < n; i += 1) {
      P[i]!.x += clamp(F[i]!.x);
      P[i]!.y += clamp(F[i]!.y);
    }
  }
  // normalize the relaxed cloud into [pad, 1-pad]
  const xs = P.map((p) => p.x);
  const ys = P.map((p) => p.y);
  const mnx = Math.min(...xs);
  const mxx = Math.max(...xs);
  const mny = Math.min(...ys);
  const mxy = Math.max(...ys);
  const span = 1 - 2 * pad;
  const pos: Positions = {};
  placed.forEach((p, i) => {
    pos[p.id] = {
      x: pad + span * ((P[i]!.x - mnx) / (mxx - mnx || 1)),
      y: pad + span * ((P[i]!.y - mny) / (mxy - mny || 1)),
    };
  });
  return pos;
}

// ─────────────────────────────────────────────────────────────────────────────
// The layout engine — `layout(nodes, edges, mode)` → Positions in [0,1].
// The five "structured" modes (tb/bt/lr/rl/radial) share the prototype's
// rank/spread parametrization; `force` runs the relaxation above. Every mode is
// deoverlapped before return.
// ─────────────────────────────────────────────────────────────────────────────

const AXIS_LO = 0.09;
const AXIS_SPAN = 0.82;
const SPREAD_LO = 0.12;
const SPREAD_SPAN = 0.76;

/**
 * Compute normalized [0,1] positions for every node under `mode`.
 *
 * @param nodes  layout nodes (only `id` required; rank/order/ocount honored)
 * @param edges  edges in any accepted shape (parent→child)
 * @param mode   one of the six LAYOUT_MODES
 */
export function layout(nodes: LayoutNode[], edges: LayoutEdge[], mode: LayoutMode): Positions {
  if (nodes.length === 0) return {};
  const known = new Set(nodes.map((n) => n.id));
  const E: [string, string][] = [];
  for (const e of edges) {
    const ne = normalizeEdge(e, known);
    if (ne) E.push(ne);
  }
  const placed = placeNodes(nodes, E);

  if (mode === "force") return deoverlap(forceLayout(placed, E));

  const maxR = Math.max(1, ...placed.map((p) => p.rank));
  const pos: Positions = {};
  for (const p of placed) {
    const r = p.rank / maxR; // 0 (root) … 1 (sources)
    const axis = AXIS_LO + AXIS_SPAN * r; // along the rank direction
    const spread = p.ocount > 1 ? SPREAD_LO + SPREAD_SPAN * (p.order / (p.ocount - 1)) : 0.5;
    let x: number;
    let y: number;
    if (mode === "bt") {
      x = spread;
      y = AXIS_LO + AXIS_SPAN * (1 - r);
    } else if (mode === "lr") {
      x = axis;
      y = spread;
    } else if (mode === "rl") {
      x = AXIS_LO + AXIS_SPAN * (1 - r);
      y = spread;
    } else if (mode === "radial") {
      if (p.rank === 0) {
        x = 0.5;
        y = 0.5;
      } else {
        // Radius grows with rank AND with how crowded the ring is, so a fat ring
        // gets pushed outward and its nodes have arc-length to breathe before the
        // deoverlap pass — the prototype's fixed 0.32 span packed dense rings.
        const crowd = Math.min(1, p.ocount / 8);
        const rad = 0.13 + 0.3 * r + 0.06 * crowd;
        // Spread a sparse ring over a wedge; a crowded ring over the full circle
        // (minus a hair) so neighbours don't collide at the seam.
        const arc = Math.PI * (1.1 + 1.7 * crowd);
        const t = p.ocount > 1 ? p.order / (p.ocount - 1) : 0.5;
        const th = Math.PI / 2 + (t - 0.5) * arc;
        x = 0.5 + rad * Math.cos(th);
        y = 0.5 + rad * Math.sin(th);
      }
    } else {
      // 'tb' default: rank runs top→bottom
      x = spread;
      y = axis;
    }
    pos[p.id] = { x, y };
  }
  return deoverlap(pos);
}

/** Convenience: compute all six layouts at once (id → mode → positions). */
export function layoutAll(nodes: LayoutNode[], edges: LayoutEdge[]): Record<LayoutMode, Positions> {
  const out = {} as Record<LayoutMode, Positions>;
  for (const m of LAYOUT_MODES) out[m] = layout(nodes, edges, m);
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Animation — the ease()/step()/requestAnimationFrame morph.
//
// `createLayoutMorph` tweens every node from a `from` Positions map to a `to`
// map over `duration` ms, invoking `onFrame(positions)` each animation frame
// with the interpolated map and `onDone()` at the end. This is what the explorer
// calls when the layout mode OR the view flips, so the nodes *glide* between
// arrangements (the "flipping and animating" the design wants) instead of
// snapping. The same cubic ease the prototype uses for `setMode()` is exported
// as `ease` for the minimap Spine↔Path↔Map slide.
// ─────────────────────────────────────────────────────────────────────────────

/** The prototype's symmetric cubic ease-in-out, t ∈ [0,1]. */
export function ease(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** Linear interpolation. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Interpolate two position maps at eased fraction `t` ∈ [0,1]. A node missing
 * from `from` enters from its `to` position (no jump); a node missing from `to`
 * holds at its `from` position. Returns a fresh map (the union of both id sets).
 */
export function interpolatePositions(from: Positions, to: Positions, t: number): Positions {
  const e = ease(Math.max(0, Math.min(1, t)));
  const out: Positions = {};
  const ids = new Set([...Object.keys(from), ...Object.keys(to)]);
  for (const id of ids) {
    const a = from[id] ?? to[id]!;
    const b = to[id] ?? from[id]!;
    out[id] = { x: lerp(a.x, b.x, e), y: lerp(a.y, b.y, e) };
  }
  return out;
}

/** A running morph; call `cancel()` to stop it early (e.g. on a new flip). */
export interface LayoutMorphHandle {
  cancel(): void;
}

export interface LayoutMorphOptions {
  /** Tween duration in ms. Default 460 (the prototype's setMode duration). */
  duration?: number;
  /** Called every frame with the interpolated positions. */
  onFrame: (positions: Positions) => void;
  /** Called once when the tween reaches `to`. */
  onDone?: () => void;
  /**
   * Injectable clock + scheduler so the morph is testable without a real
   * browser. Default to performance.now + requestAnimationFrame when available,
   * falling back to Date.now + setTimeout(16ms) (e.g. in jsdom/node).
   */
  now?: () => number;
  raf?: (cb: (t: number) => void) => number;
  caf?: (handle: number) => void;
}

const defaultNow = (): number =>
  typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();

const defaultRaf = (cb: (t: number) => void): number => {
  if (typeof requestAnimationFrame !== "undefined") return requestAnimationFrame(cb);
  return setTimeout(() => cb(defaultNow()), 16) as unknown as number;
};

const defaultCaf = (handle: number): void => {
  if (typeof cancelAnimationFrame !== "undefined") cancelAnimationFrame(handle);
  else clearTimeout(handle);
};

/**
 * Drive an eased position tween from `from` → `to`, emitting interpolated
 * Positions each frame. Mirrors the prototype's `setMode`/`setLayout` morph
 * (`ease()` + `step()` + requestAnimationFrame). Emits the first frame
 * synchronously at t=0 so the caller paints immediately, then schedules the
 * rest. A zero/negative duration jumps straight to `to`.
 */
export function createLayoutMorph(
  from: Positions,
  to: Positions,
  opts: LayoutMorphOptions,
): LayoutMorphHandle {
  const {
    duration = 460,
    onFrame,
    onDone,
    now = defaultNow,
    raf = defaultRaf,
    caf = defaultCaf,
  } = opts;

  let handle = 0;
  let cancelled = false;

  if (duration <= 0) {
    onFrame({ ...to });
    onDone?.();
    return { cancel() {} };
  }

  const t0 = now();
  // first frame, synchronously, at t=0
  onFrame(interpolatePositions(from, to, 0));

  const step = (): void => {
    if (cancelled) return;
    const t = Math.min(1, (now() - t0) / duration);
    onFrame(interpolatePositions(from, to, t));
    if (t < 1) {
      handle = raf(step);
    } else {
      onDone?.();
    }
  };
  handle = raf(step);

  return {
    cancel() {
      cancelled = true;
      caf(handle);
    },
  };
}
